import axios from 'axios'

import UserCalls from './modules/UserCalls'
import CMSCalls from '@core/api/modules/CMSCalls'
import EquipmentCalls from './modules/EquipmentCalls'
import StrainCalls from './modules/StrainCalls'
import SupplyCalls from './modules/SupplyCalls'
import ToolCalls from './modules/ToolCalls'
import LocationCalls from './modules/LocationCalls'
import { useSnackbarStore } from '@core/stores/SnackbarStore'
import TooManyRequestsError from '@core/api/error/TooManyRequestsError'

export const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ? import.meta.env.VITE_API_BASE_URL : '/api/',
  timeout: 30000,
  transformRequest: [
    function (data, headers) {
      const token = window.localStorage.AuthToken
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      if (!headers['Content-Type'] || headers['Content-Type'].startsWith('application/json')) {
        // Serialize request body as JSON unless the "Content-Type" was set to something different
        headers['Content-Type'] = 'application/json; charset=utf-8'

        return JSON.stringify(data)
      }

      return data
    },
  ],
  transformResponse: [
    function (data) {
      if (typeof data === 'string') {
        data = JSON.parse(data)
      }

      if (data.data) {
        return data.data
      }

      return data
    },
  ],
})

client.interceptors.response.use(
  response => response,
  error => {
    const snackbarStore = useSnackbarStore()

    // Default error message
    // let message = 'An unexpected error occurred'
    let message = ''

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response

      // console.log('data', data)

      switch (status) {
      // case 400:
      //   message = data?.error?.message || 'Bad request'
      //   break
      case 401:
        message = 'Unauthorized. Please login again'

        // Optionally redirect to login
        // router.push('/login')
        break
      case 403:
        message = 'You do not have permission to perform this action'
        break
      case 404:
        message = 'Resource not found'
        break

      // case 422:
      //   message = data?.error?.message || 'Validation error'
      //   break
      case 429:
        // It is up to the caller to decide how to handle such an error
        if (typeof error.response.headers['retry-after'] === 'string') {
          error = new TooManyRequestsError(Number(error.response.headers['retry-after']))
          message = ''
        } else {
          message = 'Too many requests. Please try again later'
        }
        break
      case 500:
        message = 'Server error. Please try again later'
        break
      default:
        // message = data?.error?.message || `Error: ${status}`
        break
      }
    } else if (error.request) {
      // Request was made but no response
      message = 'Network error. Please check your connection'
    } else {
      // Something else happened
      message = error.message || 'An error occurred'
    }

    if (message) {
      // Show snackbar
      snackbarStore.showError(message)
    }

    // Still reject the promise so calling code can handle it
    return Promise.reject(error)
  },
)

export const cmsClient = axios.create({
  baseURL: import.meta.env.VITE_CMS_API_BASE_URL ? `${import.meta.env.VITE_CMS_API_BASE_URL}/cms/api/` : '/cms/api/',
  timeout: 10000,
  transformResponse: [
    function (data) {
      if (typeof data === 'string') {
        data = JSON.parse(data)
      }

      if (data.data) {
        return data.data
      }

      return data
    },
  ],
})

/**
 * Adds or replaces authorization header to include the authorization token.
 * Returns modified headers.
 *
 * @param {Record<string, string|string[]>} headers
 * @param {string} token
 * @return {Record<string, string,string[]>}
 */
export function injectAuthToken(headers, token) {
  return {
    ...headers,
    'Authorization': `Bearer ${token}`,
  }
}

/**
 * @param {Record<string, string|string[]>} headers
 * @param {string} token
 * @return {Record<string, string,string[]>}
 */
export function injectReCaptchaResponse(headers, token) {
  return {
    ...headers,
    'g-recaptcha-response': token,
  }
}

export function parseRateLimitHeader(val) {
  const parts = val.split(';')
  const policy = parts[0].replaceAll('"', '')

  let remaining = null
  let reset = null
  let part

  for (let i = 1; i < parts.length; i++) {
    part = parts[i].trim().split('=')
    if (part.length !== 2) {
      continue
    }

    switch (part[0]) {
    case 'r':
      remaining = parseInt(part[1])
      break
    case 't':
      reset = parseInt(part[1])
      break
    }
  }

  return { policy, remaining, reset }
}

export const API = {
  UserCalls,
  CMSCalls,
  EquipmentCalls,
  StrainCalls,
  SupplyCalls,
  ToolCalls,
  LocationCalls,
}

export default API
